import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";

import { useProducts } from "../../hooks/queries/useProduct";
import { monospaceStyle } from "../../theme";
import { EntityClickHandler } from "../../types/entityRegistry";
import { EntityNavigationProvider } from "../Detail/EntityLink";
import { UNNAMED_PRODUCT } from "../Explore/productFields";
import { tableExportOptions } from "../../utils/export/table";
import ExportMenu from "../common/ExportMenu";
import AddProductField from "./AddProductField";
import { NOTHING, StanceMark } from "./CompareParts";
import CompareTable, { CompareTableColumn } from "./CompareTable";
import {
  SHOW_WARNINGS,
  buildCompareModel,
  countDifferences,
  tableOfCompare,
  visibleGroups,
} from "./compareModel";
import { addProduct, removeProduct, setReference } from "./compareState";
import { useCompareTray } from "./compareTray";
import { useCompareState } from "./useCompareState";

// Where the back link leads
const EXPLORE_PATH = "/explore";

// What stands for a product whose name is not known: it loads, or its read
// failed, and the tray does not know it either
const NAMELESS = "Product";

interface CompareViewProps {
  // Opens an entity in the overlay of the page around the view
  onOpenEntity: EntityClickHandler;
}

const Figure: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={monospaceStyle}>
    {children}
  </Box>
);

const surfaceSx = {
  backgroundColor: "background.paper",
  border: 1,
  borderColor: "divider",
  borderRadius: (theme: { radius: { surface: number } }) =>
    `${theme.radius.surface}px`,
} as const;

const labelSx = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "text.secondary",
} as const;

const switchLabelSx = {
  ml: 0,
  mr: 0,
  "& .MuiFormControlLabel-label": { fontSize: 13, ml: 0.5 },
} as const;

// The compare page: two to six hand-picked products as columns, everything
// on record about them as rows, each cell the statement as the sources put
// it. Which products, in which order, against which reference and under
// which switches is held in the address (see compareState); the tray
// follows the address here. No ranking and no best value: the sources do
// not support one.
const CompareView: React.FC<CompareViewProps> = ({ onOpenEntity }) => {
  const [state, change] = useCompareState();
  const tray = useCompareTray();
  const reads = useProducts(state.products);

  // The names the tray knows, for a product that is not read yet
  const trayNames = useMemo(
    () => new Map(tray.items.map((item) => [item.id, item.name])),
    [tray.items],
  );

  const columns = useMemo<CompareTableColumn[]>(
    () =>
      state.products.map((id, index) => {
        const { product = null, loading = true, error = null } =
          reads[index] ?? {};
        return {
          id,
          name: product
            ? (product.name ?? UNNAMED_PRODUCT)
            : (trayNames.get(id) ?? NAMELESS),
          product,
          loading,
          error,
        };
      }),
    [state.products, reads, trayNames],
  );

  // On this page the address is the truth: the tray is set to its products
  // on arrival and after every change, with each name once it is read. A
  // name the tray already knows is kept while its product loads.
  const { replace } = tray;
  useEffect(() => {
    replace(
      columns.map(({ id, product }) => ({
        id,
        name: product ? product.name : (trayNames.get(id) ?? null),
      })),
    );
  }, [columns, trayNames, replace]);

  const groups = useMemo(
    () =>
      buildCompareModel(
        columns.map(({ id, product }) => ({ id, product })),
        state.reference,
      ),
    [columns, state.reference],
  );
  const counts = useMemo(() => countDifferences(groups), [groups]);
  const shown = useMemo(
    () => visibleGroups(groups, state.differencesOnly),
    [groups, state.differencesOnly],
  );

  const count = state.products.length;
  const remove = (id: string) => change((current) => removeProduct(current, id));

  return (
    <EntityNavigationProvider onEntityClick={onOpenEntity}>
      <Box
        sx={{
          height: "100%",
          overflow: "auto",
          px: 3,
          pt: "22px",
          pb: 5,
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          backgroundColor: "background.default",
        }}
      >
        {/* The way back and the title. The product read names no graph and
            no date, so the page states none. */}
        <Box
          component="header"
          sx={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 1.5 }}
        >
          <MuiLink
            component={Link}
            to={EXPLORE_PATH}
            underline="hover"
            color="text.secondary"
            sx={{ fontSize: 13 }}
          >
            ← Explore
          </MuiLink>
          <Typography variant="h5" component="h1">
            Compare{" "}
            {count > 0 && (
              <>
                <Box
                  component="span"
                  sx={{ ...monospaceStyle, color: "text.secondary", fontSize: 18 }}
                >
                  {count}
                </Box>{" "}
              </>
            )}
            {count === 1 ? "product" : "products"}
          </Typography>
        </Box>

        <Box
          component="section"
          aria-label="Products compared"
          sx={{
            ...surfaceSx,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.25,
            px: 1.75,
            py: 1.25,
          }}
        >
          <Box component="span" sx={labelSx}>
            Products
          </Box>
          {columns.map((column) => {
            const isReference = column.id === state.reference;
            return (
              <Box
                key={column.id}
                component="span"
                sx={(theme) => ({
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  height: 24,
                  pl: "9px",
                  pr: "2px",
                  borderRadius: `${theme.radius.chip}px`,
                  border: "1px solid",
                  fontSize: 12.5,
                  whiteSpace: "nowrap",
                  // The reference in the accent
                  ...(isReference
                    ? {
                        borderColor: alpha(theme.palette.accent.main, 0.45),
                        backgroundColor: alpha(theme.palette.accent.main, 0.1),
                      }
                    : {
                        borderColor: alpha(theme.palette.chart.ink, 0.1),
                        backgroundColor: alpha(theme.palette.chart.ink, 0.03),
                      }),
                })}
              >
                {column.name}
                <IconButton
                  size="small"
                  aria-label={`Remove ${column.name}`}
                  onClick={() => remove(column.id)}
                  sx={{ p: "2px", color: "text.disabled" }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            );
          })}
          <AddProductField
            compared={state.products}
            onAdd={(product) => {
              // The tray learns the name at once; the address the id
              tray.add({ id: product.id, name: product.name });
              change((current) => addProduct(current, product.id));
            }}
          />
          {count > 0 && (
            <Box component="span" sx={{ ml: "auto", fontSize: 12 }}>
              <ExportMenu
                options={tableExportOptions(() => ({
                  tables: [
                    tableOfCompare(
                      shown,
                      columns.map((column) => column.name),
                    ),
                  ],
                  meta: {
                    title: "Product comparison",
                    generatedAt: new Date(),
                    filters: [
                      [
                        "Products",
                        columns.map((column) => column.name).join(", "),
                      ],
                      [
                        "Reference",
                        columns.find((column) => column.id === state.reference)
                          ?.name ?? "none",
                      ],
                      [
                        "Rows",
                        state.differencesOnly
                          ? "differences only"
                          : "all rows",
                      ],
                    ],
                  },
                  filename: ["compare"],
                }))}
              />
            </Box>
          )}
        </Box>

        {count === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Put two to six products side by side: everything on record about
            them as rows, each cell the statement as the sources put it. Add
            a product here, or pick products in Explore, on a product page or
            in the Analytics matrix.
          </Typography>
        ) : (
          <Box
            component="section"
            aria-label="Comparison"
            sx={{
              ...surfaceSx,
              px: 2.5,
              pt: 2,
              pb: 2.25,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.25,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
                <FormControlLabel
                  sx={switchLabelSx}
                  label="Differences only"
                  control={
                    <Switch
                      size="small"
                      checked={state.differencesOnly}
                      onChange={(_event, checked) =>
                        change((current) => ({
                          ...current,
                          differencesOnly: checked,
                        }))
                      }
                    />
                  }
                />
                <FormControlLabel
                  sx={switchLabelSx}
                  label="Show the sources"
                  control={
                    <Switch
                      size="small"
                      checked={state.showSources}
                      onChange={(_event, checked) =>
                        change((current) => ({
                          ...current,
                          showSources: checked,
                        }))
                      }
                    />
                  }
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box component="span" sx={{ fontSize: 13, color: "text.disabled" }}>
                    Reference
                  </Box>
                  <TextField
                    select
                    size="small"
                    value={state.reference ?? ""}
                    onChange={(event) =>
                      change((current) =>
                        setReference(current, event.target.value),
                      )
                    }
                    SelectProps={{ native: true }}
                    inputProps={{ "aria-label": "Reference" }}
                    sx={{ "& .MuiInputBase-input": { fontSize: 13, py: "5px" } }}
                  >
                    {columns.map((column) => (
                      <option key={column.id} value={column.id}>
                        {column.name}
                      </option>
                    ))}
                  </TextField>
                </Box>
              </Box>
              <Typography variant="caption" color="text.disabled">
                <span>
                  <Figure>{counts.differing}</Figure> of{" "}
                  <Figure>{counts.total}</Figure> rows differ
                </span>
                {" · a row with nothing on record for any product is left out"}
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary">
              Every cell is a statement from an article; a specification is
              its value in the row's unit · {NOTHING} means nothing on
              record, not "no" · a cell that differs from the reference
              carries the accent bar.
            </Typography>

            {count === 1 && (
              <Typography variant="body2" color="text.secondary">
                Add a second product to compare this one with.
              </Typography>
            )}

            <CompareTable
              columns={columns}
              groups={shown}
              referenceId={state.reference}
              showSources={state.showSources}
              onRemove={remove}
              onOpenEntity={onOpenEntity}
            />

            <Typography variant="caption" color="text.secondary">
              <StanceMark stance="has" decorative /> claimed ·{" "}
              <StanceMark stance="enables" decorative /> enables it in its host
              · <StanceMark stance="requires" decorative /> stated as required
              by a host or programme · <StanceMark stance="lacks" decorative />{" "}
              stated as lacking · {NOTHING} nothing on record ·{" "}
              {SHOW_WARNINGS && "⚠ two sources disagree, both kept · "}the
              accent bar marks a cell that differs from the reference · hover
              a cell for its quote and the number of its sources; "Show the
              sources" writes them under every cell.
            </Typography>
          </Box>
        )}
      </Box>
    </EntityNavigationProvider>
  );
};

export default CompareView;
